import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { OdingardLogo } from "../brand/OdingardLogo";
import { TOP_NAV, type TopLevelNav } from "../nav/navigation";
import { ProductsMenu } from "./megamenu/ProductsMenu";
import { SolutionsMenu } from "./megamenu/SolutionsMenu";
import { WardenMenu } from "./megamenu/WardenMenu";
import { IndustriesMenu } from "./megamenu/IndustriesMenu";
import { ResourcesMenu } from "./megamenu/ResourcesMenu";

type MenuKey = TopLevelNav["key"];

const PANELS: Record<MenuKey, (p: { onNavigate: () => void }) => JSX.Element> = {
  products: ProductsMenu,
  solutions: SolutionsMenu,
  warden: WardenMenu,
  industries: IndustriesMenu,
  resources: ResourcesMenu,
};

/**
 * Corporate header.
 *
 * Menus open on hover for mice and on click/Enter for keyboard and touch. The
 * two have to be reconciled: a tap fires pointerenter *and* click, so a naive
 * click-toggle opens then instantly closes the menu on touch. `openedBy` tracks
 * the origin so a click only ever closes a menu the user clicked open.
 */
export function SiteHeader() {
  const [open, setOpen] = useState<MenuKey | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const openedBy = useRef<"hover" | "click" | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const navRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  const close = useCallback(() => {
    setOpen(null);
    setMobileOpen(false);
    openedBy.current = null;
  }, []);

  useEffect(() => {
    close();
  }, [pathname, close]);

  useEffect(() => {
    if (!open && !mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [open, mobileOpen, close]);

  /** Small grace period so the pointer can cross the gap into the panel. */
  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      // A menu the user deliberately clicked open stays open until they
      // dismiss it, rather than vanishing when the pointer drifts away.
      if (openedBy.current === "click") return;
      setOpen(null);
      openedBy.current = null;
    }, 140);
  };
  const cancelClose = () => window.clearTimeout(closeTimer.current);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const Panel = open ? PANELS[open] : null;

  return (
    <header className="hdr" ref={navRef}>
      <div className="hdr__bar">
        <Link to="/" className="hdr__logo" aria-label="Odingard home">
          <OdingardLogo />
        </Link>

        <nav className="hdr__nav" aria-label="Main">
          {TOP_NAV.map((item) => (
            <div
              key={item.key}
              className="hdr__item"
              onPointerEnter={(e) => {
                if (e.pointerType !== "mouse") return;
                cancelClose();
                if (openedBy.current !== "click") {
                  setOpen(item.key);
                  openedBy.current = "hover";
                }
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === "mouse") scheduleClose();
              }}
            >
              <button
                type="button"
                className={`hdr__trigger${open === item.key ? " is-open" : ""}`}
                aria-expanded={open === item.key}
                aria-haspopup="true"
                onClick={() => {
                  const alreadyPinned =
                    open === item.key && openedBy.current === "click";
                  setOpen(alreadyPinned ? null : item.key);
                  openedBy.current = alreadyPinned ? null : "click";
                }}
              >
                {item.label}
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  aria-hidden="true"
                >
                  <path d="M1 1l4 4 4-4" />
                </svg>
              </button>
            </div>
          ))}
          <Link to="/company" className="hdr__link">
            Company
          </Link>
        </nav>

        <div className="hdr__utility">
          <Link to="/search" className="hdr__icon" aria-label="Search">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="7.8" cy="7.8" r="5.3" />
              <path d="M11.7 11.7L16 16" />
            </svg>
          </Link>
          <Link to="/demos" className="hdr__cta">
            Demos and Trials
          </Link>
          <button
            type="button"
            className="hdr__burger"
            aria-expanded={mobileOpen}
            aria-label="Menu"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {Panel && (
        <div
          className="hdr__panel"
          onPointerEnter={cancelClose}
          onPointerLeave={(e) => {
            if (e.pointerType === "mouse") scheduleClose();
          }}
        >
          <Panel onNavigate={close} />
        </div>
      )}

      {mobileOpen && (
        <div className="hdr__mobile">
          {TOP_NAV.map((item) => (
            <Link key={item.key} to={item.href} onClick={close}>
              {item.label}
            </Link>
          ))}
          <Link to="/company" onClick={close}>
            Company
          </Link>
          <Link to="/demos" className="hdr__cta" onClick={close}>
            Demos and Trials
          </Link>
        </div>
      )}
    </header>
  );
}
